/**
 * General utility helper functions
 */

export const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

export const throttle = (func, limit) => {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

export const deepMerge = (target, source) => {
  const output = Object.assign({}, target);
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!(key in target))
          Object.assign(output, { [key]: source[key] });
        else
          output[key] = deepMerge(target[key], source[key]);
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }
  return output;
};

export const isObject = (item) => {
  return item && typeof item === 'object' && !Array.isArray(item);
};

export const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return true;
    } catch (err) {
      document.body.removeChild(textArea);
      return false;
    }
  }
};

export const downloadFile = (data, filename, type = 'text/plain') => {
  const file = new Blob([data], { type });
  const url = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const parseCSV = (csvText) => {
  const lines = csvText.split('\n');
  const headers = lines[0].split(',').map(header => header.trim().replace(/"/g, ''));
  const data = [];
  
  for (let i = 1; i < lines.length; i++) {
    if (lines[i].trim()) {
      const values = lines[i].split(',').map(value => value.trim().replace(/"/g, ''));
      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      data.push(row);
    }
  }
  
  return { headers, data };
};

export const groupBy = (array, key) => {
  return array.reduce((result, currentValue) => {
    const groupKey = currentValue[key];
    (result[groupKey] = result[groupKey] || []).push(currentValue);
    return result;
  }, {});
};

export const sortBy = (array, key, direction = 'asc') => {
  return array.sort((a, b) => {
    let aVal = a[key];
    let bVal = b[key];
    
    // Handle null/undefined values
    if (aVal == null) aVal = '';
    if (bVal == null) bVal = '';
    
    // Handle different data types
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }
    
    if (direction === 'asc') {
      return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    } else {
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    }
  });
};

export const filterArray = (array, filters) => {
  return array.filter(item => {
    return Object.entries(filters).every(([key, value]) => {
      if (!value || value === '') return true;
      
      const itemValue = item[key];
      if (itemValue == null) return false;
      
      if (typeof itemValue === 'string') {
        return itemValue.toLowerCase().includes(value.toLowerCase());
      }
      
      return itemValue === value;
    });
  });
};

export const paginate = (array, page, pageSize) => {
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  
  return {
    data: array.slice(startIndex, endIndex),
    totalPages: Math.ceil(array.length / pageSize),
    totalItems: array.length,
    currentPage: page,
    hasNextPage: endIndex < array.length,
    hasPrevPage: startIndex > 0
  };
};

export const calculateProgressScore = (interview) => {
  // If processing_status exists, use it for accurate progress calculation
  if (interview.processing_status) {
    const tasks = ['scraping', 'cleaned_transcript', 'asset_class_tagging', 'location_tagging', 'ai_tags'];
    return tasks.filter(task => interview.processing_status[task] === 'completed').length;
  }

  // Fallback to legacy calculation for backward compatibility
  let score = 0;
  
  // Basic data
  if (interview.transcript_text) score++;
  
  // AI Processing stages
  if (interview.cleaned_transcript_text) score++;
  
  // Asset class tagging
  const assetFields = [
    'asset_residential', 'asset_commercial', 'asset_multi_family',
    'asset_affordable_housing', 'asset_senior_housing', 'asset_student_housing',
    'asset_hoa', 'asset_coa', 'asset_specialty_properties', 'asset_mixed_use', 'asset_storage'
  ];
  if (assetFields.some(field => interview[field])) score++;
  
  // Location tagging
  if (interview.location_tags && interview.location_tags.length > 0) score++;
  
  // AI tags
  if (interview.ai_tags && interview.ai_tags.length > 0) score++;
  
  return score;
};

export const getProgressColor = (score, maxScore = 5) => {
  const percentage = (score / maxScore) * 100;
  
  if (percentage === 100) return 'green';
  if (percentage >= 80) return 'blue';
  if (percentage >= 60) return 'yellow';
  if (percentage >= 40) return 'orange';
  return 'red';
};

export const getPriorityColor = (priority) => {
  const colors = {
    low: 'gray',
    normal: 'blue',
    high: 'orange',
    urgent: 'red'
  };
  
  return colors[priority] || 'gray';
};

export const getStatusColor = (status) => {
  const colors = {
    draft: 'gray',
    active: 'blue',
    completed: 'green',
    archived: 'yellow',
    failed: 'red',
    queued: 'blue',
    running: 'orange',
    cancelled: 'gray',
    paused: 'yellow'
  };
  
  return colors[status] || 'gray';
};

export const createQueryString = (params) => {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      searchParams.append(key, value.toString());
    }
  });
  
  return searchParams.toString();
};

export const parseQueryString = (queryString) => {
  const params = new URLSearchParams(queryString);
  const result = {};
  
  for (const [key, value] of params) {
    result[key] = value;
  }
  
  return result;
};

export const retry = async (fn, maxAttempts = 3, delay = 1000) => {
  let lastError;
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt === maxAttempts) break;
      
      await new Promise(resolve => setTimeout(resolve, delay * attempt));
    }
  }
  
  throw lastError;
};

export const createAbortController = () => {
  if (typeof AbortController !== 'undefined') {
    return new AbortController();
  }
  
  // Fallback for older browsers
  return {
    signal: null,
    abort: () => {}
  };
};

export const isArrayEqual = (arr1, arr2) => {
  if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false;
  if (arr1.length !== arr2.length) return false;
  
  return arr1.every((item, index) => item === arr2[index]);
};

export const removeEmptyFields = (obj) => {
  const cleaned = {};
  
  Object.entries(obj).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      cleaned[key] = value;
    }
  });
  
  return cleaned;
};